import { formatEuro, formatPercent } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Avatar, Banner, Pill, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useDealCommission, usePipeline } from '../api';
import type { CommissionView, DealRecord } from '../types';
import { ParticipantsCard, SideCard } from './bits';
import styles from './record.module.css';

/**
 * Deal-Akte 08 (preview with the cap) and 09 (frozen: banner, units table, „Ihr Anteil", next stages):
 * `GET /deals/:id/commission`. Hidden values („Wert nur für …") come as `visible: false`.
 */

const VALUE_HIDDEN = 'Wert nur für Closer und Provisionslinie sichtbar';

function statusVariant(status: CommissionView['status']): 'info' | 'success' | 'accent' | 'danger' {
  if (status === 'frozen') return 'success';
  if (status === 'due') return 'accent';
  if (status === 'cancelled') return 'danger';
  return 'info';
}

function SplitTable({ view }: { view: CommissionView }) {
  return (
    <div className={styles.splitTable}>
      <div className={clsx(styles.splitRow, styles.splitRowHead)}>
        <span>Beteiligte</span>
        <span className={styles.splitNum}>Teilung</span>
        <span className={styles.splitNum}>Anteil roh</span>
        <span className={styles.splitNum}>Deckel</span>
        <span className={styles.splitNum}>Anteil</span>
      </div>
      {view.rows.map((row) => (
        <div
          key={`${row.role}-${row.userId}`}
          className={clsx(styles.splitRow, row.mine && styles.splitMine)}
        >
          <span className={styles.splitPerson}>
            <Avatar
              initials={row.name
                .split(' ')
                .map((part) => part.charAt(0))
                .join('')
                .slice(0, 2)
                .toUpperCase()}
              name={row.name}
              size={26}
              tone={row.role === 'closer' ? 'navy' : 'sand'}
            />
            <span>
              <div className={styles.splitName}>{row.name}</div>
              <div className={styles.splitRole}>
                {row.roleLabel}
                {row.departed ? ' · ausgeschieden' : ''}
              </div>
            </span>
          </span>
          <span className={styles.splitNum}>
            {row.splitPct === null ? '–' : formatPercent(row.splitPct, 0)}
          </span>
          <span className={styles.splitNum}>{row.rawCents === null ? '–' : formatEuro(row.rawCents)}</span>
          <span className={styles.splitNum}>
            {row.capPct === null || row.capCents === null
              ? '–'
              : `${formatPercent(row.capPct, 1)} · ${formatEuro(row.capCents)}`}
          </span>
          <span className={styles.splitAmount}>
            {formatEuro(row.amountCents)}
            {row.note ? <div className={styles.splitNote}>{row.note}</div> : null}
          </span>
        </div>
      ))}
      <div className={clsx(styles.splitRow, styles.splitRowSum)}>
        <span>Verbleibt bei der Organisation</span>
        <span />
        <span />
        <span />
        <span className={styles.splitAmount}>
          {view.organizationCents === null ? '–' : formatEuro(view.organizationCents)}
        </span>
      </div>
    </div>
  );
}

export function CommissionTab({ deal }: { deal: DealRecord }) {
  const commission = useDealCommission(deal.id, true);
  const view = commission.data ?? null;
  const frozen = view !== null && view.status !== 'preview';
  const pipeline = usePipeline(deal.pipelineId, frozen);
  const nextStages = deal.stages.filter((stage) => stage.state === 'open').slice(0, 3);
  const responsibleFor = (stageId: string): string => {
    const info = pipeline.data;
    if (!info || info.locked) return '';
    return info.stages.find((stage) => stage.id === stageId)?.responsibleLabel ?? '';
  };

  return (
    <div className={styles.content}>
      <div className={styles.main}>
        {commission.isPending ? (
          <div className={styles.commissionTiles}>
            {[1, 2, 3].map((index) => (
              <div key={index} className={styles.tile}>
                <Skeleton width={50} height={9} />
                <div className={styles.tileValue}>
                  <Skeleton width={110} height={24} />
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {commission.isError ? <Banner tone="danger">{errorMessage(commission.error)}</Banner> : null}
        {view && !view.visible ? (
          <section className={styles.card} aria-label="Provision">
            <div className={styles.emptyBlock}>
              <div className={styles.emptyTitle}>{VALUE_HIDDEN}</div>
              <p className={styles.emptyText}>{view.hint}</p>
            </div>
          </section>
        ) : null}
        {view && view.visible ? (
          <>
            {view.frozenNotice ? (
              <div className={styles.frozenBanner} role="status">
                <span className={styles.frozenTitle}>{view.frozenNotice.title}</span>
                <span className={styles.frozenText}>{view.frozenNotice.text}</span>
              </div>
            ) : null}
            {!frozen ? (
              <div className={styles.commissionTiles}>
                <div className={styles.tile}>
                  <div className={styles.eyebrow}>Basis</div>
                  <div className={styles.tileValue}>
                    {view.baseCents === null ? '—' : formatEuro(view.baseCents)}
                  </div>
                  <div className={styles.tileSub}>
                    provisionsfähiger KP
                    {view.units.length > 0 ? ` · ${view.units.map((unit) => unit.number).join(' + ')}` : ''}
                  </div>
                </div>
                <div className={styles.tile}>
                  <div className={styles.eyebrow}>Satz</div>
                  <div className={styles.tileValue}>
                    {view.ratePct === null ? '—' : formatPercent(view.ratePct, 2)}
                  </div>
                  <div className={styles.tileSub}>
                    {deal.property ? `aus Objekt ${deal.property.name}` : 'aus dem Objekt'}
                  </div>
                </div>
                <div className={clsx(styles.tile, styles.tileDark)}>
                  <div className={styles.eyebrow}>Gesamtprovision</div>
                  <div className={styles.tileValue}>
                    {view.totalCents === null ? '—' : formatEuro(view.totalCents)}
                  </div>
                  <div className={styles.tileSub}>Vorschau · noch nicht eingefroren</div>
                </div>
              </div>
            ) : null}
            {frozen ? (
              <section className={styles.card} aria-label="Einheiten">
                <div className={styles.cardTitle}>Einheiten</div>
                <p className={styles.cardIntro}>Provision wird je Einheit berechnet und summiert.</p>
                <div className={styles.unitRows}>
                  <div className={clsx(styles.unitRow, styles.unitRowHead)}>
                    <span>Einheit</span>
                    <span className={styles.splitNum}>Basis</span>
                    <span className={styles.splitNum}>Satz</span>
                    <span className={styles.splitNum}>Provision</span>
                  </div>
                  {view.units.map((unit) => (
                    <div key={unit.number} className={styles.unitRow}>
                      <span>
                        <span className={styles.unitRowName}>{unit.number}</span>
                        {unit.detail ? <span className={styles.unitRowDetail}>{unit.detail}</span> : null}
                      </span>
                      <span className={styles.splitNum}>{formatEuro(unit.baseCents)}</span>
                      <span className={styles.splitNum}>{formatPercent(unit.ratePct, 2)}</span>
                      <span className={styles.splitAmount}>{formatEuro(unit.amountCents)}</span>
                    </div>
                  ))}
                  <div className={clsx(styles.unitRow, styles.splitRowSum)}>
                    <span className={styles.unitRowName}>Summe</span>
                    <span className={styles.splitNum}>
                      {view.baseCents === null ? '—' : formatEuro(view.baseCents)}
                    </span>
                    <span />
                    <span className={styles.splitAmount}>
                      {view.totalCents === null ? '—' : formatEuro(view.totalCents)}
                    </span>
                  </div>
                </div>
              </section>
            ) : null}
            <section className={styles.card} aria-label="Aufteilung">
              <div className={styles.splitHead}>
                <div className={styles.cardTitle}>Aufteilung</div>
                <Pill variant={statusVariant(view.status)}>{view.statusLabel}</Pill>
              </div>
              {!frozen ? (
                <p className={styles.splitFormula}>
                  Anteil = Gesamtprovision × Teilung, höchstens Basis × Deckel des Nutzers. Der Teamleiter
                  erhält die Differenz zu seinem eigenen Satz.
                </p>
              ) : null}
              <SplitTable view={view} />
            </section>
          </>
        ) : null}
      </div>
      <div className={styles.side}>
        {view && view.visible && frozen ? (
          <>
            <div className={clsx(styles.tile, styles.tileDark)}>
              <div className={styles.eyebrow}>Gesamtprovision</div>
              <div className={styles.tileValue}>
                {view.totalCents === null ? '—' : formatEuro(view.totalCents)}
              </div>
              <div className={styles.tileSub}>
                {view.units.length} {view.units.length === 1 ? 'Einheit' : 'Einheiten'} · {view.statusLabel}
              </div>
            </div>
            {view.myShareCents !== null ? (
              <div className={styles.tile}>
                <div className={styles.eyebrow}>Ihr Anteil</div>
                <div className={styles.tileValue}>{formatEuro(view.myShareCents)}</div>
                <div className={styles.tileSub}>
                  {view.status === 'due' ? 'fällig' : 'fällig nach Provisionseingang'}
                </div>
              </div>
            ) : null}
            {nextStages.length > 0 ? (
              <SideCard label="Nächste Stages">
                <div className={styles.nextStagesRows}>
                  {nextStages.map((stage) => (
                    <div key={stage.id} className={styles.nextStageRow}>
                      <span className={styles.nextStageName}>{stage.name}</span>
                      <span className={styles.nextStageOwner}>{responsibleFor(stage.id)}</span>
                    </div>
                  ))}
                </div>
              </SideCard>
            ) : null}
          </>
        ) : null}
        {view && view.visible && !frozen ? (
          <>
            {view.capNotice ? (
              <div className={styles.capBox} role="status">
                <div className={styles.capTitle}>{view.capNotice.title}</div>
                <div className={styles.capText}>{view.capNotice.text}</div>
              </div>
            ) : null}
            <SideCard label="Einfrieren">
              <p className={styles.sideText}>{view.hint}</p>
            </SideCard>
          </>
        ) : null}
        <ParticipantsCard deal={deal} />
      </div>
    </div>
  );
}
